import {
  ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import ModalTemplate from "@/shared/modal/modal-template";
import { useOpen } from "@/app/hooks";
import { Dialog, Stack, TextField } from "@mui/material";

import { useTranslation } from "react-i18next";
import { CommentModalRef } from "./types";
import { UIFormLabel } from "../../ui/ui-form-label";

export const CommentModal = forwardRef<CommentModalRef>((_, ref) => {
  const { t } = useTranslation();
  const { open, setOpen } = useOpen(false);
  const [comment, setComment] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [submitBtnText, setSubmitBtnText] = useState<string>("");

  const resolveCallback = useRef<(value: string) => void>();
  const rejectCallback = useRef(() => {});

  useImperativeHandle(ref, () => ({
    open: onOpen,
  }));

  const onOpen: CommentModalRef["open"] = (data) => {
    return new Promise((res, rej) => {
      setTitle(data?.title || "");
      setSubmitBtnText(data?.submitBtnText || "");
      setOpen(true);
      resolveCallback.current = res;
      rejectCallback.current = rej;
    });
  };

  const onClose = () => {
    setOpen(false);
    rejectCallback.current?.();

    setTimeout(() => {
      setTitle("");
      setSubmitBtnText("");
      setComment("");
    }, 300);
  };

  const onSubmit = () => {
    resolveCallback.current?.(comment);
    setOpen(false);
  };

  const onChangeComment = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setComment(value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minHeight: "45vh",
          maxWidth: 1200,
          width: "100%",
        },
      }}
    >
      <ModalTemplate
        onSubmit={onSubmit}
        submitBtnText={submitBtnText ?? t("reject")}
        onClose={onClose}
        title={title ?? t("decline_request")}
        btnSize="large"
      >
        <Stack direction="column" spacing={1}>
          <UIFormLabel required>{t("comment")}:</UIFormLabel>
          <TextField
            variant="outlined"
            rows={8}
            fullWidth
            multiline
            required
            onChange={onChangeComment}
          />
        </Stack>
      </ModalTemplate>
    </Dialog>
  );
});
